import React, { useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Search, CalendarDays, Clock, GraduationCap, Trash2, ArrowRight, Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '../AssignMentorDialog';
import { PROGRAM_PILL, PROGRAM_STATUS, STAGE_PILL, EMPTY_PROGRAM, ProgramDialog, Spinner, Empty, formatDayYear } from './shared';

const STAGE_FILTERS = [
  ['active', 'Active programs'], ['ready_to_close', 'Ready to close'], ['upcoming', 'Upcoming'],
  ['closed', 'Closed'], ['all', 'All programs'],
];
const METRIC_LABEL = {
  companies: 'participating companies', approved_opportunities: 'approved opportunities',
  applicants: 'student applicants', placed: 'students placed', completed: 'internships completed',
};

/** Searchable list of every internship programme, live or historical. */
export default function AllProgramsTab({ data, loading, onOpenProgram, onRefresh, editing, setEditing, metric, onClearMetric }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(metric && metric !== 'programs' ? 'all' : 'active');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    return (data?.programs || []).filter(p => {
      if (filter === 'active' && (!p.open || p.stage === 'ready_to_close')) return false;
      if (filter === 'ready_to_close' && p.stage !== 'ready_to_close') return false;
      if (filter === 'upcoming' && !(p.status !== 'closed' && p.start_date > today)) return false;
      if (filter === 'closed' && p.status !== 'closed') return false;
      if (metric && METRIC_LABEL[metric] && !p.metrics[metric === 'placed' ? 'placements' : metric]) return false;
      return !q || [p.name, p.description].filter(Boolean).some(v => v.toLowerCase().includes(q));
    });
  }, [data, query, filter, metric]);

  const remove = async (p) => {
    try {
      await api.delete(`/coordinator/internship-programs/${p.id}`);
      toast.success('Programme deleted');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not delete this programme'); }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="all-programs-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search programmes"
            className="h-9 pl-8 rounded-lg border-gray-200 text-xs bg-white" data-testid="programs-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-[210px] rounded-lg border-gray-200 text-xs bg-white" data-testid="programs-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STAGE_FILTERS.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setEditing(EMPTY_PROGRAM)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" data-testid="new-program-btn">
          <Plus className="h-3.5 w-3.5 mr-1" />New Program
        </Button>
      </div>

      {metric && METRIC_LABEL[metric] && (
        <button onClick={onClearMetric} className="mb-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#F06651] bg-[#FFF0ED] rounded-lg px-2.5 py-1" data-testid="metric-filter-chip">
          Showing programmes with {METRIC_LABEL[metric]} · clear
        </button>
      )}

      {rows.length === 0 ? (
        <Empty testId="programs-empty" text={query.trim() || filter !== 'all' || metric
          ? 'No programmes match this search.'
          : 'No internship programmes yet. Create one to start collecting opportunities from companies and mentors.'} />
      ) : (
        <div className="space-y-2">
          {rows.map(p => (
            <div key={p.id} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3" data-testid={`program-row-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <button onClick={() => onOpenProgram(p.id, 'overview')} className="min-w-0 text-left flex-1" data-testid={`open-program-${p.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#1F2937]">{p.name}</p>
                    <Badge className={`border-0 text-[10px] ${PROGRAM_PILL[p.status]}`} data-testid={`program-status-${p.id}`}>
                      {PROGRAM_STATUS.find(s => s.value === p.status)?.label}
                    </Badge>
                    {!['closed', 'draft'].includes(p.stage) && (
                      <Badge className={`border-0 text-[10px] ${STAGE_PILL[p.stage]}`} data-testid={`program-stage-${p.id}`}>{p.stage_label}</Badge>
                    )}
                    {p.action_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#F06651]" data-testid={`program-actions-${p.id}`}>
                        <Bell className="h-3 w-3" />{p.action_count}
                      </span>
                    )}
                  </div>
                  {p.description && <p className="text-sm text-[#6B7280] mt-1 line-clamp-1">{p.description}</p>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#4B5563]">
                    <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3 text-[#9CA3AF]" />{formatDayYear(p.start_date)} – {formatDayYear(p.end_date)}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#9CA3AF]" />Apply by {formatDayYear(p.application_deadline)}</span>
                    <span className="flex items-center gap-1"><GraduationCap className="h-3 w-3 text-[#9CA3AF]" />Grades {(p.eligible_grades || []).join(', ')}</span>
                  </div>
                  <p className="text-[11px] text-[#9CA3AF] mt-1.5" data-testid={`program-metrics-${p.id}`}>
                    {p.metrics.companies} compan{p.metrics.companies === 1 ? 'y' : 'ies'} · {p.metrics.approved_opportunities} of {p.metrics.opportunities} opportunities approved ·
                    {' '}{p.metrics.applicants} applicant{p.metrics.applicants === 1 ? '' : 's'} · {p.metrics.placements} placed · {p.metrics.completed} completed
                  </p>
                </button>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => setEditing(p)} disabled={p.lifecycle?.locked} title={p.lifecycle?.lock_reason || ''} data-testid={`edit-program-${p.id}`}>Edit</Button>
                  <Button size="sm" className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-lg text-xs" onClick={() => onOpenProgram(p.id, 'overview')} data-testid={`manage-program-row-${p.id}`}>
                    Manage<ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                  {p.metrics.opportunities === 0 && (
                    <Button size="sm" variant="ghost" className="rounded-lg text-xs text-red-500 hover:bg-red-50" onClick={() => remove(p)} data-testid={`delete-program-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <ProgramDialog program={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onRefresh(); }} />}
    </div>
  );
}
